import { useEffect, useMemo, useRef, useState } from "react";
import { Empty } from "antd";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import RulesValidationModalV2 from "./RulesValidationModalV2";
import cuReviewForm from "../../data/cuReview_form.json";
import {
    buildInitialValues,
    ExcelRow,
    parseCuHistory,
    stringifyCuHistory,
    isSameAsLastForCuType,
    buildHistoryEntry,
    isoToMdy,
    HISTORY_UI_ID,
    mdyHmToDatetimeLocal,
    datetimeLocalToMdyHm,
    CU1_CLEAR_FIELD_IDS,
    CU2_CLEAR_FIELD_IDS,
    APP1_REVISION_CLEAR_FIELD_IDS,
    APP2_REVISION_CLEAR_FIELD_IDS,
    buildFcdHistoryEntry,
    formatFcdHistoryEntry
} from "./cuReviewFormHelpers";

import { fieldReader, fieldWriter } from "../../services/loanApi";
import { getAccessToken } from "../../services/authApi";
import { runRules } from "../../services/rulesApi";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";
import {
    getPagePersonaAccess,
    evaluatePageAndFieldAccess
} from "../../services/personaApi";

export default function CuReviewPage() {
    const form = cuReviewForm;

    const [token, setToken] = useState(null);
    const [loanid, setLoanid] = useState("");

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");

    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState("success");
    const [toastMessage, setToastMessage] = useState("");
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");

    const [values, setValues] = useState(() => buildInitialValues(form));
    const savedValuesRef = useRef(null);
    const savedLoanIdRef = useRef("");
    const [userName, setUserName] = useState("");
    const [userId, setUserId] = useState("");
    const [isPageReadOnly, setIsPageReadOnly] = useState(false);
    const [readOnlyMessage, setReadOnlyMessage] = useState("");

    const [restrictedFieldIds, setRestrictedFieldIds] = useState([]);
    const [canEditRestrictedFields, setCanEditRestrictedFields] = useState(false);

    const [rulesModalOpen, setRulesModalOpen] = useState(false);
    const [rulesModalResult, setRulesModalResult] = useState(null);
    const [pendingSaveData, setPendingSaveData] = useState(null);

    const parseBool = (v) => {
        if (v == null) return false;
        const s = String(v).trim().toLowerCase();
        return s === "true" || s === "1" || s === "yes" || s === "y";
    };

    // Vite example: VITE_SkipEmptyField=true
    // CRA example: REACT_APP_SkipEmptyField=true
    const SKIP_EMPTY_FIELD = parseBool(process.env?.REACT_APP_SKIP_EMPTY_FIELD);

    const appraisalReceived =
        (values["cu_app1_receivedDate"] || "").trim() !== "" ||
        (values["cu_app2_receivedDate"] || "").trim() !== "";

    const showToastMsg = (msg, type = "success") => {
        setToastMessage(msg);
        setToastType(type);
        setToastVisible(true);
        setToastAnimation("animate-fade-in-right");

        setTimeout(() => {
            setToastAnimation("animate-fade-out-right");
            setTimeout(() => setToastVisible(false), 500);
        }, 8000);
    };

    const enableAppraisal2 = (values["cu_app_required"] || "") === "Y";

    const onChange = (id, v) => {
        setValues(prev => {
            const next = { ...prev, [id]: v };
            const who = (userName || "").trim();
            if (!who) return next;

            // ---- APP1 Revisions ----
            if (id === "cu_app1_reqDate") next["cu_app1_reqBy"] = v ? who : "";

            // ---- APP2 Revisions ----
            if (id === "cu_app2_reqDate") next["cu_app2_reqBy"] = v ? who : "";

            // ---- CU1 ----
            if (id === "cu1_reqDate") next["cu1_reqBy"] = v ? who : "";
            if (id === "cu1_reviewedDate") next["cu1_reviewedBy"] = v ? who : "";

            // ---- CU2 ----
            if (id === "cu2_reqDate") next["cu2_reqBy"] = v ? who : "";
            if (id === "cu2_reviewedDate") next["cu2_reviewedBy"] = v ? who : "";

            // ---- CU2 decision => clear algorithm section (cu_algo_*) ----
            if (id === "cu2_reviewDecision") {
                allFields
                    .filter(f => (f?.id || "").startsWith("cu_algo_"))
                    .forEach(f => { next[f.id] = ""; });
            }

            // ---- FCD Review Date => FCD Reviewer Name ----
            if (id === "cu_fcd_reviewerDate") next["cu_fcd_reviewerName"] = v ? who : "";

            return next;
        });
    };

    useEffect(() => {
        const who = (userName || "").trim();
        if (!who) return;

        setValues(prev => {
            let changed = false;
            const next = { ...prev };

            const setIfNeeded = (dateId, byId) => {
                const d = (next[dateId] || "").trim();
                const b = (next[byId] || "").trim();

                if (d && b !== who) { next[byId] = who; changed = true; }
                if (!d && b) { next[byId] = ""; changed = true; }
            };

            // Revisions
            setIfNeeded("cu_app1_reqDate", "cu_app1_reqBy");
            setIfNeeded("cu_app2_reqDate", "cu_app2_reqBy");

            // CU1/CU2
            setIfNeeded("cu1_reqDate", "cu1_reqBy");
            setIfNeeded("cu1_reviewedDate", "cu1_reviewedBy");
            setIfNeeded("cu2_reqDate", "cu2_reqBy");
            setIfNeeded("cu2_reviewedDate", "cu2_reviewedBy");

            return changed ? next : prev;
        });
    }, [userName, token, loanid]);

    const sections = useMemo(() => Array.isArray(form?.sections) ? form.sections : [], [form]);

    // ---- helpers: gather fields + map encompass <-> ui ----
    const allFields = useMemo(() => {
        const list = [];
        (form?.sections || []).forEach(sec => {
            (sec?.fields || []).forEach(f => list.push(f));
        });
        return list;
    }, [form]);

    const uiFieldsWithEncompass = useMemo(() => {
        return allFields.filter(f => !!f?.encompassId);
    }, [allFields]);

    const encompassIds = useMemo(() => {
        // only real ids (skip blanks), unique
        return [...new Set(uiFieldsWithEncompass.map(f => f.encompassId).filter(Boolean))];
    }, [uiFieldsWithEncompass]);

    const mmddyyyyToIso = (s) => {
        if (!s) return "";
        const str = String(s).trim();
        if (str === "//") return "";

        // already ISO?
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

        const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!m) return ""; // unknown format
        const [, mm, dd, yyyy] = m;
        return `${yyyy}-${mm}-${dd}`;
    };

    const normalizeFromEncompass = (field, raw) => {
        if (raw == null) return "";
        if (raw === "//") return "";

        const str = String(raw).trim();

        if (field?.control === "date") {
            return mmddyyyyToIso(str);
        }

        if (field?.control === "datetime") {
            return mdyHmToDatetimeLocal(str);
        }

        if (field?.control === "radio") {
            if (str === "Y") return "Yes";
            if (str === "N") return "No";
        }

        return str;
    };

    const normalizeForEncompassWrite = (field, raw) => {
        if (raw == null) return "";
        let val = String(raw).trim();

        if (field?.control === "decimal") {
            val = val.replace(/,/g, "");
        }

        if (field?.control === "datetime") {
            // Encompass-friendly string
            val = datetimeLocalToMdyHm(val);
        }

        if (field?.control === "radio") {
            if (val === "Yes") return "Y";
            if (val === "No") return "N";
        }

        if (val === "") return "";
        return val;
    };

    // Keep appraisalReceived gating if you still want to block both until appraisal is received.
    // If you truly want ONLY required flag, then remove `!appraisalReceived` parts.
    const disableCu1 = !appraisalReceived;
    const disableCu2 = !appraisalReceived;

    const isCu1RequestReady =
        (values["cu1_reqDate"] || "").trim() !== "" &&
        (values["cu1_reqType"] || "").trim() !== "";

    const isCu2RequestReady =
        (values["cu2_reqDate"] || "").trim() !== "" &&
        (values["cu2_reqType"] || "").trim() !== "";

    const applyHistoryAndClearRules = (currentValues) => {
        const next = { ...currentValues };

        if (!(next["cu_fcd_status"] || "").trim()) {
            next["cu_fcd_status"] = "Decision Pending";
        }

        // Existing stored JSON (CX.CUREVIEW.HISTORY)
        const historyRaw = next[HISTORY_UI_ID] || "";
        const historyArr = parseCuHistory(historyRaw);

        const actionBy = (userName || "").trim();

        // Algo reasons (Yes) used only for CU2 entries
        const algoReasons = allFields
            .filter((f) => f?.id?.startsWith("cu_algo_") && next[f.id] === "Yes")
            .map((f) =>
                String(f.label || "")
                    .replace(/^CU Second Level\[/, "")
                    .replace(/\]$/, "")
            );

        const appendUniqueLine = (text, line) => {
            const t = (text || "").trimEnd();
            const lines = t ? t.split("\n").map(x => x.trim()).filter(Boolean) : [];
            const last = lines.length ? lines[lines.length - 1] : "";

            if (last === line.trim()) return text || ""; // skip duplicate
            return (text ? text + "\n" : "") + line;
        };

        /* =========================
           REVISION HISTORY (APP1)
           - ONLY update textarea
           - DO NOT push into CU history JSON
           - DO NOT clear here (clear after successful save)
        ========================= */
        const app1RevisionType = (next["cu_app1_revisionType"] || "").trim();
        const app1RevisionReqDate = (next["cu_app1_reqDate"] || "").trim();
        const app1RevisionRequired = (next["cu_app1_revRequired"] || "").trim();

        if (app1RevisionType && app1RevisionReqDate && actionBy) {
            const revisionLine =
                `${isoToMdy(app1RevisionReqDate)} - ${app1RevisionType} requested by ${actionBy}` +
                (app1RevisionRequired ? ` || Revision Required: ${app1RevisionRequired}` : "");

            next["cu_app1_revisionHistory"] = appendUniqueLine(
                next["cu_app1_revisionHistory"],
                revisionLine
            );
        }

        /* =========================
           REVISION HISTORY (APP2)
           - DO NOT clear here (clear after successful save)
        ========================= */
        const app2RevisionType = (next["cu_app2_revisionType"] || "").trim();
        const app2RevisionReqDate = (next["cu_app2_reqDate"] || "").trim();
        const app2RevisionRequired = (next["cu_app2_revRequired"] || "").trim();

        if (app2RevisionType && app2RevisionReqDate && actionBy) {
            const revisionLine =
                `${isoToMdy(app2RevisionReqDate)} - ${app2RevisionType} requested by ${actionBy}` +
                (app2RevisionRequired ? ` || Revision Required: ${app2RevisionRequired}` : "");

            next["cu_app2_revisionHistory"] = appendUniqueLine(
                next["cu_app2_revisionHistory"],
                revisionLine
            );
        }

        /* =====================================================
           CU1 LOGIC
           - when request date + type exist => log Requested
           - when reviewed date + decision exist => log Completed and clear CU1 fields
        ===================================================== */
        const cu1Decision = (next["cu1_reviewDecision"] || "").trim();
        const cu1RequestType = (next["cu1_reqType"] || "").trim();
        const cu1RequestedDate = (next["cu1_reqDate"] || "").trim();
        const cu1ReviewedDate = (next["cu1_reviewedDate"] || "").trim();
        const cu1ReqNote = (next["cu1_reqNote"] || "").trim();
        const cu1RevNote = (next["cu1_reviewNote"] || "").trim();

        if (cu1RequestType && cu1RequestedDate && actionBy) {
            const entry = buildHistoryEntry({
                actionDateIso: cu1RequestedDate,
                cuType: "CU1",
                actionType: "Requested",
                actionBy,
                requestType: cu1RequestType,
                decision: "",
                requestNote: cu1ReqNote
            });

            if (!isSameAsLastForCuType(historyArr, entry)) {
                historyArr.push(entry);
            }
        }

        if (cu1Decision && cu1ReviewedDate && actionBy) {
            const entry = buildHistoryEntry({
                actionDateIso: cu1ReviewedDate,
                cuType: "CU1",
                actionType: "Completed",
                actionBy,
                requestType: cu1RequestType,
                decision: cu1Decision,
                reviewNote: cu1RevNote
            });

            if (!isSameAsLastForCuType(historyArr, entry)) {
                historyArr.push(entry);
            }

            CU1_CLEAR_FIELD_IDS.forEach(id => {
                next[id] = "";
            });
        }

        /* =====================================================
           CU2 LOGIC
           - when request date + type exist => log Requested (with algoReasons)
           - when reviewed date + decision exist => log Completed and clear CU2 fields
        ===================================================== */
        const cu2Decision = (next["cu2_reviewDecision"] || "").trim();
        const cu2RequestType = (next["cu2_reqType"] || "").trim();
        const cu2RequestedDate = (next["cu2_reqDate"] || "").trim();
        const cu2ReviewedDate = (next["cu2_reviewedDate"] || "").trim();
        const cu2ReqNote = (next["cu2_reqNote"] || "").trim();
        const cu2RevNote = (next["cu2_reviewNote"] || "").trim();

        if (cu2RequestType && cu2RequestedDate && actionBy) {
            const entry = buildHistoryEntry({
                actionDateIso: cu2RequestedDate,
                cuType: "CU2",
                actionType: "Requested",
                actionBy,
                requestType: cu2RequestType,
                decision: "",
                algoReasons,
                requestNote: cu2ReqNote
            });

            if (!isSameAsLastForCuType(historyArr, entry)) {
                historyArr.push(entry);
            }
        }

        if (cu2Decision && cu2ReviewedDate && actionBy) {
            const entry = buildHistoryEntry({
                actionDateIso: cu2ReviewedDate,
                cuType: "CU2",
                actionType: "Completed",
                actionBy,
                requestType: cu2RequestType,
                decision: cu2Decision,
                algoReasons,
                reviewNote: cu2RevNote
            });

            if (!isSameAsLastForCuType(historyArr, entry)) {
                historyArr.push(entry);
            }

            // clear CU2 fields
            CU2_CLEAR_FIELD_IDS.forEach(id => {
                next[id] = "";
            });

            // clear Informational Fields from algorithm too
            allFields
                .filter(f => (f?.id || "").startsWith("cu_algo_"))
                .forEach(f => {
                    next[f.id] = "";
                });
        }

        // Compare the fields that will actually be written with the last successful read/save.
        const fcdFields = allFields.filter(f => f.id?.startsWith("cu_fcd_") && f.encompassId && f.isUpdate !== false);
        const fcdEntry = buildFcdHistoryEntry(fcdFields, savedValuesRef.current, next, actionBy, normalizeForEncompassWrite, SKIP_EMPTY_FIELD);
        if (fcdEntry) historyArr.push(fcdEntry);

        /* =====================================================
           WRITE BACK HISTORY JSON
        ===================================================== */
        next[HISTORY_UI_ID] = stringifyCuHistory(historyArr);

        return { nextValues: next, historyArr };
    };

    // make buildChangesPayload accept a values object
    const buildChangesPayload = (vals) => {
        const byEncompassId = new Map();
        const updatable = uiFieldsWithEncompass.filter(f => f.isUpdate !== false);

        for (const f of updatable) {
            const eid = f.encompassId;
            if (!eid) continue;

            const uiVal = vals?.[f.id] ?? "";
            const normalized = normalizeForEncompassWrite(f, uiVal);

            // if field is Date and empty, don't send (when flag enabled)
            if (SKIP_EMPTY_FIELD && f?.control === "date" && normalized === "") {
                continue;
            }

            if (!byEncompassId.has(eid)) {
                byEncompassId.set(eid, { id: eid, value: normalized, _hasNonEmpty: normalized !== "" });
            } else {
                const prev = byEncompassId.get(eid);
                if (normalized !== "") {
                    byEncompassId.set(eid, { id: eid, value: normalized, _hasNonEmpty: true });
                } else if (!prev._hasNonEmpty) {
                    byEncompassId.set(eid, { id: eid, value: normalized, _hasNonEmpty: false });
                }
            }
        }

        return [...byEncompassId.values()].map(({ _hasNonEmpty, ...rest }) => rest);
    };

    useEffect(() => {
        const initGuestScriptAsync = async () => {
            if (window.elli?.script?.guest) {
                try {
                    window.elli.script.guest.create();

                    // --- Get User ---
                    const tpoAuthObject = await window.elli.script.getObject("auth");
                    const user = await tpoAuthObject.getUser();
                    setUserName(user.firstName + " " + user.lastName);
                    setUserId(user.id || "");

                    const pageAccess = await getPagePersonaAccess("CuReviewPage");
                    const accessResult = evaluatePageAndFieldAccess(pageAccess, user);

                    setIsPageReadOnly(accessResult.isPageReadOnly);
                    setRestrictedFieldIds(accessResult.restrictedFieldIds || []);
                    setCanEditRestrictedFields(accessResult.canEditRestrictedFields);

                    if (accessResult.isPageReadOnly) {
                        setReadOnlyMessage("CU Review is view-only for Account Executive persona.");
                    } else {
                        setReadOnlyMessage("");
                    }

                    // --- Get Loan ---
                    const loanObject = await window.elli.script.getObject("loan");
                    const loan = await loanObject.all();

                    setLoanid(loan.loan.id);

                    await getToken();

                } catch (error) {
                    const errorCode = logError("CU_REVIEW_LOAN_FETCH_ERROR");

                    logAppInsightError("Loan Fetch Error", {
                        component: "CuReviewPage",
                        problemId: errorCode,
                        errorCode,
                        rawError: error,
                        errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                    });

                    setLoading(false);
                }
            } else {
                console.warn("elli.script.guest is not available.");
            }
        };

        initGuestScriptAsync();

    }, []);

    const isFieldForceReadOnly = (field) => {
        const encompassId = String(field?.encompassId || "").trim().toLowerCase();
        if (!encompassId) return false;

        const restricted = restrictedFieldIds.map(x => String(x || "").trim().toLowerCase());
        const isRestrictedField = restricted.includes(encompassId);

        if (!isRestrictedField) return false;

        return !canEditRestrictedFields;
    };

    const getToken = async () => {
        setLoading(true);
        setLoadingText("Loading CU Review...");
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const tkn = await getAccessToken(authCode);
            setToken(tkn);
        } catch (err) {
            const errorCode = logError("CU_REVIEW_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "CuReviewPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    // ---- read encompass fields ----
    const fetchFields = async () => {
        if (!token || !loanid) return;
        if (!encompassIds.length) return;

        setLoading(true);
        setLoadingText("Loading CU Review...");
        try {
            const data = await fieldReader(token, encompassIds, loanid);

            // Keep a persisted baseline so loading or an unchanged Save cannot create history.
            const loadedValues = {};
            for (const f of uiFieldsWithEncompass) {
                loadedValues[f.id] = normalizeFromEncompass(f, data?.[f.encompassId]);
            }
            savedValuesRef.current = loadedValues;
            savedLoanIdRef.current = loanid;
            setValues(prev => ({ ...prev, ...loadedValues }));
        } catch (err) {
            const errorCode = logError("CU_REVIEW_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "CuReviewPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
            console.error("CU Review field fetch failed", err);
            showToastMsg("Failed to load CU Review fields.", "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    useEffect(() => {
        if (!token) return;
        fetchFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, loanid]);

    const historyRows = useMemo(() => {
        // values[cu_History] is the UI field bound to CX.CUREVIEW.HISTORY
        return parseCuHistory(values?.cu_History);
    }, [values]);

    const getEncompassErrorDetails = (e) => {
        const errors = e?.response?.data?.errors;
        if (!Array.isArray(errors) || !errors.length) return null;

        // show all "details" lines
        return errors
            .map(x => x?.details)
            .filter(Boolean)
            .join("\n"); // keep each error on new line
    };


    // ---- save/write encompass fields ----
    const onSave = async (isRetry = false) => {
        if (isPageReadOnly) {
            showToastMsg(readOnlyMessage || "You have read-only access to CU Review.", "error");
            return;
        }

        if (!token || !loanid) {
            showToastMsg("Missing token or loan id.", "error");
            return;
        }

        if (!savedValuesRef.current || savedLoanIdRef.current !== loanid) {
            showToastMsg("Load CU Review fields successfully before saving.", "error");
            return;
        }

        const hasApp1RevisionToClear =
            (values["cu_app1_revisionType"] || "").trim() !== "" &&
            (values["cu_app1_reqDate"] || "").trim() !== "" &&
            (userName || "").trim() !== "";

        const hasApp2RevisionToClear =
            (values["cu_app2_revisionType"] || "").trim() !== "" &&
            (values["cu_app2_reqDate"] || "").trim() !== "" &&
            (userName || "").trim() !== "";

        // 1) apply rules: append history json + keep revision fields for first save
        const { nextValues } = applyHistoryAndClearRules(values);

        // Commit history to the UI only after the field write succeeds.
        // Cancelled validation and failed saves must not leave unsaved history rows.

        // 2) first payload = save entered values + revision history + CU history
        const updatedFields = buildChangesPayload(nextValues);

        // 3) prepare cleared values for second save (only after first save succeeds)
        const valuesAfterClear = { ...nextValues };

        if (hasApp1RevisionToClear) {
            APP1_REVISION_CLEAR_FIELD_IDS.forEach(id => {
                valuesAfterClear[id] = "";
            });
        }

        if (hasApp2RevisionToClear) {
            APP2_REVISION_CLEAR_FIELD_IDS.forEach(id => {
                valuesAfterClear[id] = "";
            });
        }

        const enableRuleValidation = parseBool(process.env?.REACT_APP_ENABLE_RULE_VALIDATION);

        if (!enableRuleValidation) {
            // Skip rule validation, proceed directly to save
            setPendingSaveData({ updatedFields, nextValues, valuesAfterClear, hasApp1RevisionToClear, hasApp2RevisionToClear, isRetry });
            await continueSave({ updatedFields, nextValues, valuesAfterClear, hasApp1RevisionToClear, hasApp2RevisionToClear, isRetry });
            return;
        }

        setLoading(true);
        setLoadingText("Validating rules...");

        // Run rules validation before saving
        try {
            const rulesEncompassIds = ["CX.APPR.ACREAGE", "CX.APPR.PROP.CONDITION"];
            const rulesFields = uiFieldsWithEncompass
                .filter(f => rulesEncompassIds.includes(f.encompassId) && (nextValues[f.id] || "").trim() !== "")
                .map(f => ({
                    id: f.encompassId,
                    value: normalizeForEncompassWrite(f, nextValues[f.id])
                }));

            const fcdStatusField = uiFieldsWithEncompass.find(f => f.encompassId === "CX.APPR.FCD.STATUS");
            const fcdStatusValue = fcdStatusField ? (nextValues[fcdStatusField.id] || "").trim() : "";
            const showFatalAsWarning = fcdStatusValue !== "Decision Complete";

            const rulesResult = await runRules({
                loanId: loanid,
                contextName: process.env.REACT_APP_CU_RULESET_NAME || "CU_FINAL_DETERMINATION",
                showFatalAsWarning,
                fields: rulesFields,
                requestedBy: userId
            });

            setLoading(false);
            setLoadingText("");

            // Store pending save data and show rules modal
            setPendingSaveData({ updatedFields, nextValues, valuesAfterClear, hasApp1RevisionToClear, hasApp2RevisionToClear, isRetry });
            setRulesModalResult(rulesResult);
            setRulesModalOpen(true);

        } catch (rulesErr) {
            console.warn("Rules validation failed, proceeding with save.", rulesErr);
            setLoading(false);
            setLoadingText("");

            // Show modal with error info, still allow save
            setPendingSaveData({ updatedFields, nextValues, valuesAfterClear, hasApp1RevisionToClear, hasApp2RevisionToClear, isRetry });
            setRulesModalResult({
                overallStatus: "ERROR",
                summary: { passCount: 0, warningCount: 0, fatalCount: 0, errorCount: 1 },
                groups: [],
                errorMessage: "Rules validation failed. You can still continue to save."
            });
            setRulesModalOpen(true);
        }
    };

    const continueSave = async (saveDataOverride) => {
        setRulesModalOpen(false);
        const saveData = (saveDataOverride && saveDataOverride.updatedFields) ? saveDataOverride : pendingSaveData;
        const { updatedFields, nextValues, valuesAfterClear, hasApp1RevisionToClear, hasApp2RevisionToClear, isRetry } = saveData;
        setPendingSaveData(null);
        setRulesModalResult(null);

        setLoading(true);
        setLoadingText("Saving CU Review...");

        try {
            // First save: save revision fields + revision history + CU data
            await fieldWriter(token, loanid, updatedFields);
            // Advance the baseline after the first successful write, even if revision cleanup fails.
            const persistedValues = { ...savedValuesRef.current };
            const writtenFields = new Map(updatedFields.map(f => [f.id, f.value]));
            for (const f of uiFieldsWithEncompass) {
                if (writtenFields.has(f.encompassId)) persistedValues[f.id] = normalizeFromEncompass(f, writtenFields.get(f.encompassId));
            }
            savedValuesRef.current = persistedValues;
            savedLoanIdRef.current = loanid;
            setValues(nextValues);

            // Second save: clear revision entry fields after successful first save
            if (hasApp1RevisionToClear || hasApp2RevisionToClear) {
                const clearPayload = buildChangesPayload(valuesAfterClear);
                await fieldWriter(token, loanid, clearPayload);
                setValues(valuesAfterClear);
            }

            await fetchFields();
            showToastMsg("CU Review saved successfully.", "success");
        } catch (e) {
            if (e?.response?.status === 401 && !isRetry) {
                console.warn("401 detected, regenerating token...");
                await getToken();
                return onSave(true);
            }

            const errorCode = logError("CU_REVIEW_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "CuReviewPage",
                problemId: errorCode,
                errorCode,
                rawError: e,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            const allDetails = getEncompassErrorDetails(e);
            const serverMsg = e?.response?.data?.details;

            showToastMsg(
                allDetails ? `Save failed:\n${allDetails}` :
                    (serverMsg ? `Save failed: ${serverMsg}` : "Save failed."),
                "error"
            );
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };




    const renderOnePanelAppraisal = (section) => {
        const fields = section.fields || [];

        return (
            <div className="border rounded-lg bg-white">
                {/* Title/Heading in Row 1 */}
                {section.titleLeft && (
                    <div className="text-center font-semibold text-sm py-2 bg-sky-200">
                        {section.titleLeft}
                    </div>
                )}

                <div className="p-4">
                    {/* Row 2 with 2 sub-rows */}
                    <div className="grid grid-cols-1 gap-6">


                        {fields.length ? (
                            fields.map(f => (
                                <div key={f.id} className="mb-4">
                                    {/* Row 2 - 1st Sub-row: Textarea Heading */}
                                    <div className="form-label">{f.label}</div>

                                    {/* Row 2 - 2nd Sub-row: Textarea Control */}
                                    <div>
                                        <textarea

                                            value={values[f.id] || ''}
                                            onChange={(e) => onChange(f.id, e.target.value)}
                                            className="w-full p-2 border rounded-lg"
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <Empty />
                        )}



                    </div>
                </div>
            </div>
        );
    };


    const renderOnePanelTwoColumnAppraisal = (section, sectionDisabled = false) => {
        const fields = section.fields || [];

        return (
            <div className="border rounded-lg bg-white">
                {section.titleLeft && (
                    <div className="text-center font-semibold text-sm py-2 bg-sky-200">
                        {section.titleLeft}
                    </div>
                )}

                <div className="p-4">
                    <div className="border rounded-lg">
                        <div className="p-2 grid grid-cols-2 gap-1">
                            {fields.length ? (
                                fields.map((f) => (
                                    <div
                                        key={f.id}
                                        className={f.fullWidth ? "col-span-full" : ""}
                                    >
                                        <ExcelRow
                                            field={f}
                                            label={f.label}
                                            pageReadOnly={isPageReadOnly}
                                            fieldForceReadOnly={isFieldForceReadOnly(f)}
                                            sectionDisabled={
                                                sectionDisabled ||
                                                (
                                                    section.titleLeft === "CU1" &&
                                                    ["cu1_reviewedDate", "cu1_reviewDecision", "cu1_reviewedBy"].includes(f.id) &&
                                                    !isCu1RequestReady
                                                ) ||
                                                (
                                                    section.titleLeft === "CU2" &&
                                                    ["cu2_reviewedDate", "cu2_reviewDecision", "cu2_reviewedBy"].includes(f.id) &&
                                                    !isCu2RequestReady
                                                )
                                            }
                                            value={values[f.id]}
                                            onChange={onChange}
                                        />
                                    </div>
                                ))
                            ) : (
                                <Empty />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTwoPanelRightEmptyAppraisal = (section) => {
        const fields = section.fields || [];

        return (
            <div className="border rounded-lg bg-white">
                {section.titleLeft && (
                    <div className="grid grid-cols-2">
                        <div className="text-center font-semibold text-sm py-2 bg-sky-200 border-r">
                            {section.titleLeft}
                        </div>
                        <div className="py-2 bg-gray-100" />
                    </div>
                )}

                <div className="p-4">
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left column */}
                        <div className="border rounded-lg">
                            <div className="p-2">
                                {fields.length ? (
                                    fields.map(f => (
                                        <ExcelRow
                                            key={f.id}
                                            label={f.label}
                                            field={f}
                                            value={values[f.id]}
                                            onChange={onChange}
                                            pageReadOnly={isPageReadOnly}
                                            fieldForceReadOnly={isFieldForceReadOnly(f)}
                                        />
                                    ))
                                ) : (
                                    <Empty />
                                )}
                            </div>
                        </div>

                        {/* Right column (empty placeholder) */}
                        <div className="border rounded-lg bg-gray-50">
                            <div className="p-2 min-h-[00px]" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTwoPanelAppraisal = (section, disableRightPanel = false, disableLeftPanel = false) => {

        const allSectionFields = section.fields || [];

        // 🔹 Field above header (Required Y/N)
        const aboveHeaderFields = allSectionFields.filter(
            f => f.headerSlot === "above-header-right"
        );

        // 🔹 Normal panel fields
        const leftFields = allSectionFields.filter(
            f => f.panel === "left" && !f.headerSlot
        );

        const rightFields = allSectionFields.filter(
            f => f.panel === "right" && !f.headerSlot
        );

        return (
            <div className="border rounded-lg bg-white">

                {/* ===== ABOVE HEADER ROW ===== */}
                {aboveHeaderFields.length > 0 && (
                    <div className="px-4 pt-2">
                        <div className="grid grid-cols-2 gap-6">

                            {/* Left empty column (same width as left panel) */}
                            <div></div>

                            {/* Right panel width EXACT match */}
                            <div className="p-2">
                                {aboveHeaderFields.map(field => (
                                    <ExcelRow
                                        key={field.id}
                                        field={field}
                                        value={values[field.id]}
                                        onChange={onChange}
                                        pageReadOnly={isPageReadOnly}
                                        fieldForceReadOnly={isFieldForceReadOnly(field)}
                                    />
                                ))}
                            </div>


                        </div>
                    </div>
                )}

                {/* ===== HEADER ROW ===== */}
                {(section.titleLeft || section.titleRight) && (
                    <div className="grid grid-cols-2">
                        <div className="text-center font-semibold text-sm py-2 bg-sky-200 border-r">
                            {section.titleLeft}
                        </div>
                        <div className="text-center font-semibold text-sm py-2 brand-bg">
                            {section.titleRight}
                        </div>
                    </div>
                )}

                {/* ===== BODY ===== */}
                <div className="p-4">
                    <div className="grid grid-cols-2 gap-6">

                        <div className="border rounded-lg">
                            <div className="p-2">
                                {leftFields.length ? (
                                    leftFields.map(f => (
                                        <ExcelRow
                                            key={f.id}
                                            field={f}
                                            value={values[f.id]}
                                            onChange={onChange}
                                            pageReadOnly={isPageReadOnly}
                                            fieldForceReadOnly={isFieldForceReadOnly(f)}
                                            sectionDisabled={disableLeftPanel}
                                        />
                                    ))
                                ) : (
                                    <Empty />
                                )}
                            </div>
                        </div>

                        <div className="border rounded-lg">
                            <div className="p-2">
                                {rightFields.length ? (
                                    rightFields.map(f => (
                                        <ExcelRow
                                            key={f.id}
                                            field={f}
                                            value={values[f.id]}
                                            pageReadOnly={isPageReadOnly}
                                            fieldForceReadOnly={isFieldForceReadOnly(f)}
                                            onChange={onChange}
                                            sectionDisabled={disableRightPanel}
                                        />
                                    ))
                                ) : (
                                    <Empty />
                                )}
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        );
    };

    const renderTwoColumns = (section) => {
        const left = (section.fields || []).filter(f => (f.col || "left") === "left");
        const right = (section.fields || []).filter(f => f.col === "right");

        return (
            <div className="border rounded-lg bg-white">
                {section.title ? (
                    <div className="text-center font-semibold text-sm py-2 bg-sky-200 border-b">
                        {section.title}
                    </div>
                ) : null}

                <div className="p-4">
                    <div className="grid grid-cols-2 gap-6">
                        <div className="border rounded-lg">
                            <div className="p-2">
                                {left.map(f => (
                                    <ExcelRow
                                        key={f.id}
                                        label={f.label}
                                        field={f}
                                        value={values[f.id]}
                                        onChange={onChange}
                                        pageReadOnly={isPageReadOnly}
                                        fieldForceReadOnly={isFieldForceReadOnly(f)}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="border rounded-lg">
                            <div className="p-2">
                                {right.map(f => (
                                    <ExcelRow
                                        key={f.id}
                                        label={f.label}
                                        field={f}
                                        value={values[f.id]}
                                        onChange={onChange}
                                        pageReadOnly={isPageReadOnly}
                                        fieldForceReadOnly={isFieldForceReadOnly(f)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const buildLogLine = (r) => {
        if (r.cuType === "FCD") return formatFcdHistoryEntry(r);
        const date = isoToMdy(r.actionDate);

        const reqNote = (r.requestNote || "").trim();
        const revNote = (r.reviewNote || "").trim();

        const noteSuffixParts = [];
        if (reqNote) noteSuffixParts.push(`Request Notes: ${reqNote}`);
        if (revNote) noteSuffixParts.push(`Review Notes: ${revNote}`);

        const notesSuffix = noteSuffixParts.length ? ` | ${noteSuffixParts.join(" | ")}` : "";

        // ---- APPRAISAL REVISIONS ----
        if (r.cuType === "APP1" || r.cuType === "APP2") {
            if (r.actionType === "Revision Requested") {
                return `${date} ${r.cuType} revision "${r.requestType}" was requested by ${r.actionBy}`;
            }
            return `${date} ${r.cuType} ${r.actionType} by ${r.actionBy}`;
        }

        // ---- CU1 ----
        if (r.cuType === "CU1") {
            if (r.actionType === "Requested") {
                return `${date} CU1 was requested by ${r.actionBy} for "${r.requestType}"${notesSuffix}`;
            }
            return `${date} CU1 was completed by ${r.actionBy} and decision was "${r.decision}"${notesSuffix}`;
        }

        // ---- CU2 ----
        const reasons = (r.algoReasons || []).join(", ");
        if (r.cuType === "CU2") {
            if (r.actionType === "Requested") {
                return `${date} CU2 of type "${r.requestType}" was requested by ${r.actionBy}${reasons ? ` for ${reasons}` : ""}${notesSuffix}`;
            }
            return `${date} CU2 of type "${r.requestType}" was completed by ${r.actionBy} and decision was "${r.decision || ""}"${reasons ? ` for ${reasons}` : ""}${notesSuffix}`;
        }

        return "";
    };

    return (
        <div className="rr-page space-y-6 p-25px">
            <LoadingOverlay loading={loading} text={loadingText} />

            <div className="rounded-lg p-4 space-y-6 max-w-8xl mx-auto">
                <div className="flex items-center justify-between">
                    <div className="page-title1"></div>
                    {isPageReadOnly && (
                        <div className="border border-yellow-300 bg-yellow-50 text-yellow-800 rounded-lg px-4 py-3">
                            {readOnlyMessage || "This page is read-only for your persona."}
                        </div>
                    )}
                    {/* Floating Save Button */}
                    {!isPageReadOnly && (

                        <div className="fixed top-4 right-4 z-50">
                            <button
                                className="btn-base btn-next shadow-lg"
                                style={{
                                    color: "red",
                                    backgroundColor: "yellow",
                                    border: "1px solid red"
                                }}

                                onClick={() => onSave(false)}
                                disabled={loading}
                            >
                                Click here to Save
                            </button>
                        </div>
                    )}
                </div>

                {sections.map((sec) => {

                    if (sec.key === "CUHistorySection") {
                        return (
                            <div key={sec.key} className="border rounded-lg bg-white">
                                <div className="text-center font-semibold text-sm py-2 bg-sky-200 border-b">
                                    CU Review History
                                </div>
                                <div className="p-4 space-y-2 text-sm min-h-[50px] max-h-[200px] overflow-y-auto">
                                    {historyRows.length === 0 ? (
                                        <div className="text-gray-500">No activity yet.</div>
                                    ) : (
                                        historyRows.map((r, idx) => (
                                            <div key={idx}>
                                                {buildLogLine(r)}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        );
                    }

                    if (sec.layout === "one-panel") return <div key={sec.key}>{renderOnePanelAppraisal(sec)}</div>;
                    if (sec.key === "CU1Section1")
                        return <div key={sec.key}>
                            {renderOnePanelTwoColumnAppraisal(sec, disableCu1)}
                        </div>;

                    if (sec.key === "CU2Section1")
                        return <div key={sec.key}>
                            {renderOnePanelTwoColumnAppraisal(sec, disableCu2)}
                        </div>;
                    if (sec.layout === "two-panels-right-empty") return <div key={sec.key}>{renderTwoPanelRightEmptyAppraisal(sec)}</div>;
                    if (sec.layout === "two-panels") {
                        let disableRight = false;

                        if (sec.key === "appraisals") {
                            disableRight = !enableAppraisal2;
                        }

                        if (sec.key === "CU1Section") {
                            const rightDisabled = false;  // enable CU2 only when required yes
                            const leftDisabled = false;    // disable CU1 when required yes

                            return (
                                <div key={sec.key}>
                                    {renderTwoPanelAppraisal(sec, rightDisabled, leftDisabled)}
                                </div>
                            );
                        }

                        return (
                            <div key={sec.key}>
                                {renderTwoPanelAppraisal(sec, disableRight)}
                            </div>
                        );
                    }
                    if (sec.layout === "two-columns") return <div key={sec.key}>{renderTwoColumns(sec)}</div>;
                    return null;
                })}


                <div className="flex items-center justify-between">
                    <div className="page-title1"></div>
                    {!isPageReadOnly && (
                        <div className="flex items-center gap-3">
                            <button className="btn-base btn-next" onClick={() => onSave(false)} disabled={loading}>
                                Save
                            </button>
                        </div>
                    )}
                </div>

                <Toast
                    visible={toastVisible}
                    type={toastType}
                    message={toastMessage}
                    onClose={() => setToastVisible(false)}
                    animationClass={toastAnimation}
                />

                <RulesValidationModalV2
                    isOpen={rulesModalOpen}
                    result={rulesModalResult}
                    onClose={() => {
                        setRulesModalOpen(false);
                        setPendingSaveData(null);
                        setRulesModalResult(null);
                    }}
                    onContinueSave={continueSave}
                />
            </div>
        </div>
    );
}